import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'phosphor-react';
import { toast } from 'sonner';
import { IKanbanCard, IKanbanColumn } from '../../../../hooks/useKanban';
import { ILegalizacao, TipoLegalizacao, useLegalizacao } from '../../../../hooks/useLegalizacao';
import {
  arrasteDeEtapa,
  checklistEfetivoDaEtapa,
  detalheDoErro,
  etapaConfigurada,
  nivelRisco,
  proximaAcao,
  itemSatisfeito,
  QUEM_META,
  RISCO_META,
  COR_TIPO,
  fluxoDaEtapa,
  regimeDoProcesso,
  rotaDoProcesso,
  naoSeraCliente,
  tiposAlteracaoDoProcesso,
} from '../legalizacaoPlaybook';
import { CardDoProcesso } from './CardDoProcesso';
import { FinalizarProcessoModal } from './FinalizarProcessoModal';
import { FluxoIcons } from './FluxoIcons';
import { LegalizacaoCardEntregue } from './LegalizacaoCardEntregue';

interface LegalizacaoBoardProps {
  columns: IKanbanColumn[];
  legByCardId: Map<number, ILegalizacao>;
  tipo: TipoLegalizacao;
  busca?: string;
  /**
   * A vista está restrita por QUALQUER motivo, inclusive o recorte de estado persistido.
   * Governa o TEXTO da coluna vazia: com filtro, ela não está vazia, está escondida.
   *
   * Prop explícita, e não `onIniciarAqui={undefined}`: o board recebe `busca` mas não os
   * outros eixos, então não tem como calcular isto sozinho — e um gate implícito não
   * diria a ninguém por que o botão sumiu.
   */
  filtroAtivo?: boolean;
  /**
   * Filtro que o operador ligou NESTA sessão (busca, situação, pendência) — não inclui o
   * recorte de estado, que é persistido e volta sozinho a cada abertura.
   *
   * Governa a AÇÃO e o MOVIMENTO: esconder o "Iniciar aqui" e rolar até o primeiro
   * resultado. Usar `filtroAtivo` aqui fazia quem fixou um estado perder o botão para
   * sempre, e o quadro saltar pro início a cada invalidação de query.
   */
  filtroTransitorio?: boolean;
  // eslint-disable-next-line no-unused-vars
  onCardClick: (card: IKanbanCard, leg: ILegalizacao) => void;
  // "Iniciar aqui": cria um processo direto nesta coluna (etapas anteriores assumidas).
  // eslint-disable-next-line no-unused-vars
  onIniciarAqui?: (colunaTitulo: string) => void;
}

/**
 * O quadro rola pelo TRILHO DE ETAPAS (logo acima dele), não por barra de scroll.
 *
 * Antes havia DUAS barras fazendo o mesmo trabalho: a nativa do quadro e uma espelhada
 * no topo, que existia só pra não obrigar a descer até o rodapé dos cards pra rolar.
 * Duas faixas cinzas com quatro setinhas do Windows, e nenhuma delas dizia nada além de
 * "você está mais ou menos aqui".
 *
 * O trilho substitui as duas: cada segmento é uma ETAPA, na largura da coluna, e o que
 * está aceso é o que cabe na tela. Vira orientação além de navegação — dá pra ver
 * quantas etapas ficaram de fora e pular direto pra uma.
 *
 * ⚠️ Rolagem que continua funcionando sem barra: shift+roda, trackpad horizontal,
 * setas do teclado com o quadro focado, e o próprio trilho (clique e arraste).
 */
const SCROLLBAR_OCULTA = {
  '&::-webkit-scrollbar': { display: 'none' },
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
};

/**
 * Medidas da barra de rolagem do quadro.
 *
 * PISTA é a área que responde a mouse; POLEGAR é o traço que se vê. Mantê-los separados
 * é o que dá alvo confortável sem engordar o desenho: a pista cresce (alvo), o polegar
 * cresce pouco (peso visual). A faixa reservada é sempre a mesma, então o quadro não
 * pula de altura quando a barra some.
 */
/**
 * Largura da coluna. O Estatuto usa 288px; aqui fica 248px porque a legalização tem até 8
 * etapas e a coluna larga esconderia etapas demais. A faixa de observação do card foi medida
 * nesta largura (`FaixaObservacao`): mudou aqui, reconferir lá.
 */
const LARGURA_COLUNA = '248px';

const PISTA_H = '16px';
const POLEGAR_H = '9px';
/** Centraliza o polegar na pista. Derivado, pra os três números não saírem de sincronia. */
const POLEGAR_TOP = `${(parseFloat(PISTA_H) - parseFloat(POLEGAR_H)) / 2}px`;
/**
 * Piso da largura do polegar. Com muitas colunas a largura proporcional viraria um traço
 * fino demais pra pegar.
 *
 * ⚠️ Usado em TRÊS contas (pintar, arrasto e clique na pista). Divergir entre elas faz o
 * polegar escorregar do cursor durante o arrasto — por isso é constante, não literal.
 */
const POLEGAR_MIN_W = 44;

/**
 * Ordem da coluna FINAL: entrega mais recente primeiro.
 *
 * Nas demais colunas a ordem é o `rank` (LexoRank), que reflete arrastes manuais e é o que
 * faz o arrasto ter sentido. Na coluna terminal ninguém ordena nada à mão — o rank ali é
 * resíduo de como o card entrou, e o resultado era uma lista sem critério legível.
 *
 * `moved_at` é o carimbo da última movimentação; na coluna final ele É a data de entrega.
 * Card sem carimbo (anterior ao campo) vai pro fim em vez de embaralhar com os datados.
 */
const entradaNaColuna = (c: IKanbanCard) => (c.moved_at ? Date.parse(c.moved_at) : NaN);
const entregaMaisRecentePrimeiro = (a: IKanbanCard, b: IKanbanCard) => {
  const ta = entradaNaColuna(a);
  const tb = entradaNaColuna(b);
  const semA = Number.isNaN(ta);
  const semB = Number.isNaN(tb);
  if (semA && semB) return 0;
  if (semA) return 1;
  if (semB) return -1;
  return tb - ta;
};

// Estado do modal de confirmação do move (drag gated).
interface ConfirmState {
  card: IKanbanCard;
  leg: ILegalizacao;
  fromCol: string;
  toCol: string;
  direction: 'forward' | 'backward';
  faltantes: string[];
}

export const LegalizacaoBoard: React.FC<LegalizacaoBoardProps> = ({
  columns,
  legByCardId,
  tipo,
  busca,
  filtroAtivo,
  filtroTransitorio,
  onCardClick,
  onIniciarAqui,
}) => {
  const boardRef = useRef<HTMLDivElement>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [finalizando, setFinalizando] = useState<{ card: IKanbanCard; leg: ILegalizacao } | null>(
    null,
  );
  const { moverLegalizacao, finalizarLegalizacao } = useLegalizacao();
  const queryClient = useQueryClient();

  /**
   * Quais observações estão expandidas, por id de card.
   *
   * ⚠️ MORA NO BOARD, de propósito. O `@hello-pangea/dnd` desmonta e remonta o componente
   * do card durante o arrasto (o Draggable troca de árvore ao entrar no portal de
   * arrasto), então um `useState` dentro do card voltaria ao colapsado no meio do
   * movimento — a obs que o operador acabou de abrir pra decidir pra onde mover sumiria
   * exatamente na hora de mover. Indexado por `card.id` e não por posição: a coluna final
   * é reordenada e o índice não identifica card.
   */
  const [obsAbertas, setObsAbertas] = useState<Record<number, boolean>>({});
  const alternarObs = useCallback(
    (cardId: number) => setObsAbertas(m => ({ ...m, [cardId]: !m[cardId] })),
    [],
  );

  const pistaRef = useRef<HTMLDivElement>(null);
  const barraRef = useRef<HTMLDivElement>(null);
  const sombraEsqRef = useRef<HTMLDivElement>(null);
  const sombraDirRef = useRef<HTMLDivElement>(null);
  const arrasto = useRef<{ x0: number; scroll0: number; pista: number } | null>(null);
  const rafRef = useRef<number | null>(null);

  /**
   * Posiciona a barra e as sombras ESCREVENDO DIRETO NO DOM, sem estado do React.
   *
   * ⚠️ Isto é o que segura os frames. A versão anterior guardava a posição da rolagem
   * em `useState` e atualizava no `onScroll` — ou seja, o quadro INTEIRO re-renderizava
   * a cada evento de rolagem, e cada card refaz `checklistEfetivoDaEtapa` +
   * `proximaAcao` + `resolverRegistry` no render. Com ~30 cards isso é trabalho demais
   * pra caber num frame, e a rolagem lateral engasgava enquanto a vertical (que não
   * dispara nada disso) seguia lisa. O código original fazia igual a isto aqui: uma
   * escrita direta no DOM, zero re-render.
   *
   * `transform: translateX` em vez de `left`: translate é resolvido no compositor, sem
   * recalcular layout a cada frame.
   */
  const pintar = useCallback(() => {
    const el = boardRef.current;
    const barra = barraRef.current;
    const pista = pistaRef.current;
    if (!el || !barra || !pista) return;
    const pistaW = pista.clientWidth;
    const max = el.scrollWidth - el.clientWidth;
    if (max <= 4 || pistaW <= 0) {
      barra.style.display = 'none';
      // Quadro que não rola não tem para onde apontar: as sombras apagam junto com a barra.
      if (sombraEsqRef.current) sombraEsqRef.current.style.opacity = '0';
      if (sombraDirRef.current) sombraDirRef.current.style.opacity = '0';
      return;
    }
    barra.style.display = 'block';
    // O curso desconta a largura real do polegar, então o fim da pista continua batendo
    // com o fim do quadro mesmo quando o piso de largura entra em ação.
    const larguraPx = Math.max(POLEGAR_MIN_W, (el.clientWidth / el.scrollWidth) * pistaW);
    barra.style.width = `${larguraPx}px`;
    barra.style.transform = `translateX(${(el.scrollLeft / max) * (pistaW - larguraPx)}px)`;
    if (sombraEsqRef.current) sombraEsqRef.current.style.opacity = el.scrollLeft > 8 ? '1' : '0';
    if (sombraDirRef.current)
      sombraDirRef.current.style.opacity = el.scrollLeft < max - 8 ? '1' : '0';
  }, []);

  // Um repinte por frame no máximo. O navegador dispara `scroll` mais vezes que isso.
  const aoRolar = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      pintar();
    });
  }, [pintar]);

  useEffect(() => {
    pintar();
    window.addEventListener('resize', pintar);
    return () => {
      window.removeEventListener('resize', pintar);
      // ⚠️ ZERAR O REF, não só cancelar. `aoRolar` usa `rafRef.current !== null` como
      // "já tem frame agendado" e retorna cedo. Este efeito re-roda a cada mudança de
      // `columns` — ou seja, a cada invalidação de query. Se o cleanup rodasse com um
      // frame pendente, o callback (que é quem zera o ref) nunca executaria, o ref
      // ficaria preso num id morto e a barra PARARIA de acompanhar a rolagem para
      // sempre, sem erro nenhum.
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [columns, pintar]);

  // Arrasto da barra: converte pixels percorridos na PISTA em pixels de rolagem. O
  // fator é (curso rolável ÷ curso da barra) — sem ele a barra andaria mais devagar
  // que o cursor, porque a pista mede a largura visível, não a total do quadro.
  const iniciarArrasto = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = boardRef.current;
    if (!el) return;
    arrasto.current = {
      x0: e.clientX,
      scroll0: el.scrollLeft,
      pista: pistaRef.current?.clientWidth ?? 0,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const moverArrasto = (e: React.PointerEvent<HTMLDivElement>) => {
    const a = arrasto.current;
    const el = boardRef.current;
    if (!a || !el) return;
    const max = el.scrollWidth - el.clientWidth;
    const larguraPx = Math.max(POLEGAR_MIN_W, (el.clientWidth / el.scrollWidth) * a.pista);
    const cursoBarra = a.pista - larguraPx;
    if (cursoBarra <= 0) return;
    el.scrollLeft = a.scroll0 + ((e.clientX - a.x0) * max) / cursoBarra;
  };
  const soltarArrasto = (e: React.PointerEvent<HTMLDivElement>) => {
    arrasto.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  /**
   * Clique na PISTA salta pra aquele ponto do quadro (polegar centrado no cursor).
   *
   * Com a pista maior, a maior parte dela é área que o cursor acerta sem querer acertar —
   * clicar e não acontecer nada leria como barra quebrada. É também o caminho mais rápido
   * pra ir do começo ao fim do quadro: um clique em vez de arrastar o percurso inteiro.
   *
   * O `e.target !== e.currentTarget` deixa passar só o clique na PISTA: o polegar é filho
   * e tem handler próprio, e sem esse teste ele saltaria antes de começar a arrastar.
   */
  const saltarNaPista = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    const el = boardRef.current;
    const pista = pistaRef.current;
    if (!el || !pista) return;
    const max = el.scrollWidth - el.clientWidth;
    const pistaW = pista.clientWidth;
    if (max <= 4 || pistaW <= 0) return;
    const larguraPx = Math.max(POLEGAR_MIN_W, (el.clientWidth / el.scrollWidth) * pistaW);
    const curso = pistaW - larguraPx;
    if (curso <= 0) return;
    const x = e.clientX - pista.getBoundingClientRect().left - larguraPx / 2;
    const destino = (Math.min(Math.max(x, 0), curso) / curso) * max;
    el.scrollTo({ left: destino, behavior: 'smooth' });
  };

  // Rola até a 1ª coluna com resultado quando o filtro muda.
  //
  // `columns` aqui já chega filtrado do pai, então "1ª coluna com card de legalização"
  // É a 1ª coluna com resultado — não precisa reimplementar o predicado da busca.
  //
  // Gatilho é `filtroAtivo`, não `busca`: filtrar por "onde está a pendência" pode deixar o
  // único resultado numa etapa avançada, e sem isto o operador ficaria olhando quatro
  // colunas vazias achando que o filtro não achou nada.
  useEffect(() => {
    if (!filtroTransitorio || !boardRef.current) return;
    const idx = columns.findIndex(c => (c.cards ?? []).some(card => legByCardId.has(card.id)));
    if (idx > 0) boardRef.current.scrollTo({ left: idx * 260, behavior: 'smooth' });
  }, [busca, filtroTransitorio, columns, legByCardId]);

  const moverMutation = useMutation({
    mutationFn: (v: { id: number; toCol: string }) =>
      moverLegalizacao(v.id, { coluna_titulo: v.toCol }),
    onSuccess: (_data, v) => {
      toast.success(`Movido para "${v.toCol}"`);
      queryClient.invalidateQueries({ queryKey: ['legalizacoes'] }); // prefixo: per-tipo + dashboard
      queryClient.invalidateQueries({ queryKey: ['kanban-board'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-atividades'] });
      setConfirm(null);
    },
    onError: () => toast.error('Não foi possível mover. Tente novamente.'),
  });

  const finalizarMutation = useMutation({
    mutationFn: (id: number) => finalizarLegalizacao(id),
    onSuccess: () => {
      toast.success('Processo finalizado');
      queryClient.invalidateQueries({ queryKey: ['legalizacoes'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-board'] });
      // O card finalizado passa a vir na lista de arquivados (é de lá que sai "Finalizados").
      queryClient.invalidateQueries({ queryKey: ['legalizacao-arquivados'] });
      setFinalizando(null);
    },
    onError: (erro: unknown) =>
      toast.error(detalheDoErro(erro, 'Não foi possível finalizar. Tente novamente.')),
  });

  /** O card da coluna final está com o checklist fechado? (mesma regra do botão do card) */
  const prontoParaFinalizar = (card: IKanbanCard, coluna: string): boolean => {
    const leg = legByCardId.get(card.id);
    if (!leg) return false;
    const estado = ((leg.estado_etapas as Record<string, Record<string, unknown>> | null)?.[
      coluna
    ] ?? {}) as Record<string, unknown>;
    return (
      proximaAcao(tipo, coluna, estado, {
        uf: leg.junta_uf,
        municipio: leg.municipio,
        tiposAlteracao: tiposAlteracaoDoProcesso(leg.estado_etapas),
        regime: regimeDoProcesso(leg.estado_etapas),
      }).quem === 'nenhum'
    );
  };

  // Drag GATED: 1 etapa por vez, sempre passando pelo modal de confirmação
  // (avançar exige o checklist da etapa; voltar só confirma). Pular etapas → recusa.
  const onDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    const fromIdx = columns.findIndex(c => `col-${c.id}` === source.droppableId);
    const toIdx = columns.findIndex(c => `col-${c.id}` === destination.droppableId);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return; // mesma coluna: ordem não importa
    const cardId = Number(draggableId.replace('card-', ''));
    const leg = legByCardId.get(cardId);
    const card = columns[fromIdx].cards.find(c => c.id === cardId);
    if (!leg || !card) return;
    // Uma etapa por vez, contando só as que se aplicam — a Rota B atravessa as `somenteRotaA`, e o "não será
    // cliente" Demais órgãos e o Licenciamento da constituição.
    const salto = arrasteDeEtapa(
      tipo,
      columns.map(c => c.titulo),
      fromIdx,
      toIdx,
      rotaDoProcesso(leg.estado_etapas, leg.rota),
      naoSeraCliente(leg.estado_etapas),
    );
    if (!salto) return;
    if (!salto.ok) {
      let erro = 'Avance uma etapa por vez.';
      if (salto.motivo === 'nao_se_aplica')
        erro =
          tipo === 'alteracao'
            ? 'Esta etapa não se aplica à Rota B.'
            : 'Esta etapa não se aplica: a empresa não será cliente.';
      toast.error(erro);
      return;
    }
    const fromCol = columns[fromIdx].titulo;
    const toCol = columns[toIdx].titulo;
    if (salto.direcao === 'forward') {
      // Fail-CLOSED: coluna sem bloco no PLAYBOOK (título do backend fora de sincronia com
      // o front) devolveria checklist vazio → zero faltantes → o drag passaria batido.
      if (!etapaConfigurada(tipo, fromCol)) {
        toast.error(`Etapa "${fromCol}" não está configurada no playbook — avanço bloqueado.`);
        return;
      }
      const estado = ((leg.estado_etapas as Record<string, Record<string, unknown>> | null)?.[
        fromCol
      ] ?? {}) as Record<string, unknown>;
      const faltantes = checklistEfetivoDaEtapa(
        tipo,
        fromCol,
        leg.junta_uf,
        leg.municipio,
        tiposAlteracaoDoProcesso(leg.estado_etapas),
        regimeDoProcesso(leg.estado_etapas),
      )
        .filter(c => c.req && !itemSatisfeito(estado, c.key))
        .map(c => c.label);
      setConfirm({ card, leg, fromCol, toCol, direction: 'forward', faltantes });
    } else {
      setConfirm({ card, leg, fromCol, toCol, direction: 'backward', faltantes: [] });
    }
  };

  const forwardBloqueado = confirm?.direction === 'forward' && confirm.faltantes.length > 0;
  const cor = COR_TIPO[tipo];

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <Box>
          {/* Faixa da barra. A barra é sempre visível; quem some é só ela mesma quando
              não há o que rolar (`display` controlado no `pintar`). A faixa fica
              reservada de qualquer jeito pra o quadro não pular de altura.
              A pista tem trilho próprio (o fundo): sem ele, uma pista alta e invisível é
              área clicável que não se anuncia. */}
          <Box
            position="relative"
            ref={pistaRef}
            h={PISTA_H}
            mb={1}
            mx={1}
            borderRadius="full"
            bg="blackAlpha.50"
            cursor="pointer"
            onPointerDown={saltarNaPista}
          >
            <Box
              ref={barraRef}
              position="absolute"
              top={POLEGAR_TOP}
              left={0}
              h={POLEGAR_H}
              borderRadius="full"
              bg="gray.300"
              // NADA de transition em transform/width: a posição é reescrita a cada
              // frame durante a rolagem, e animar isso faria a barra correr atrás do
              // quadro em vez de acompanhá-lo.
              transition="background .15s"
              cursor="grab"
              _hover={{ bg: 'gray.400' }}
              _active={{ cursor: 'grabbing', bg: 'gray.400' }}
              onPointerDown={iniciarArrasto}
              onPointerMove={moverArrasto}
              onPointerUp={soltarArrasto}
              sx={{ touchAction: 'none', willChange: 'transform' }}
            />
          </Box>

          {/* O quadro, com sombra interna nas bordas quando há conteúdo pra aquele lado.
              É sombra e não gradiente de propósito: gradiente precisa acertar a cor exata
              do fundo, e qualquer divergência vira uma faixa visível. Sombra interna
              escurece o que estiver atrás, seja qual for.
              A opacidade das duas é escrita direto no DOM pelo `pintar`, não por estado. */}
          <Box position="relative">
            <Box
              ref={sombraEsqRef}
              position="absolute"
              left={0}
              top={0}
              bottom="16px"
              w="24px"
              pointerEvents="none"
              zIndex={1}
              opacity={0}
              boxShadow="inset 14px 0 12px -12px rgba(15,23,42,.22)"
              transition="opacity .2s"
            />
            <Box
              ref={sombraDirRef}
              position="absolute"
              right={0}
              top={0}
              bottom="16px"
              w="24px"
              pointerEvents="none"
              zIndex={1}
              opacity={0}
              boxShadow="inset -14px 0 12px -12px rgba(15,23,42,.22)"
              transition="opacity .2s"
            />
            <Flex
              ref={boardRef}
              gap={3}
              overflowX="auto"
              pb={4}
              px={1}
              align="flex-start"
              minH="calc(100vh - 300px)"
              onScroll={aoRolar}
              sx={SCROLLBAR_OCULTA}
            >
              {columns.map((col, idxCol) => {
                const doQuadro = (col.cards ?? []).filter(c => legByCardId.has(c.id));
                const ehFinal = idxCol === columns.length - 1;
                // Só a coluna FINAL é reordenada — as outras continuam no rank, que é o
                // que o arrasto manipula. Cópia antes de ordenar: `col.cards` vem do cache
                // do react-query e ordenar no lugar mutaria dado compartilhado.
                const cards = ehFinal ? [...doQuadro].sort(entregaMaisRecentePrimeiro) : doQuadro;
                const prontos = ehFinal
                  ? cards.filter(c => prontoParaFinalizar(c, col.titulo)).length
                  : 0;
                return (
                  <Box
                    key={col.id}
                    minW={LARGURA_COLUNA}
                    maxW={LARGURA_COLUNA}
                    flexShrink={0}
                    bg="gray.50"
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor="gray.200"
                    p={3}
                  >
                    {/* Cabeçalho no padrão das colunas do Estatuto: bolinha na cor do tipo, título
                      e contagem. Os ícones de fluxo (quem age na etapa) são da legalização. */}
                    <Flex align="center" gap={2} px={1} mb={2.5}>
                      <Box w="8px" h="8px" borderRadius="full" bg={cor.border} flexShrink={0} />
                      <Text
                        fontSize="xs"
                        fontWeight="700"
                        color="gray.700"
                        letterSpacing="0.02em"
                        lineHeight="1.2"
                        noOfLines={2}
                        minW={0}
                      >
                        {col.titulo}
                      </Text>
                      <Box ml="auto" flexShrink={0}>
                        <FluxoIcons markers={fluxoDaEtapa(tipo, col.titulo)} size={12} />
                      </Box>
                      <Box
                        bg="white"
                        borderWidth="1px"
                        borderColor="gray.200"
                        borderRadius="full"
                        px={2}
                        py={0.5}
                        flexShrink={0}
                      >
                        <Text
                          fontSize="11px"
                          fontWeight="700"
                          color="gray.500"
                          lineHeight="1.2"
                          sx={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {cards.length}
                        </Text>
                      </Box>
                    </Flex>
                    {/* TERMÔMETRO DA COLUNA (2026-08-11): composição de risco da etapa,
                      em uma barra de 4px. Dá pra ver qual coluna está pegando fogo sem
                      ler card nenhum. Coluna vazia fica só com a trilha. */}
                    {/* Na coluna FINAL a barra não fala de risco (o processo já foi entregue):
                      mostra quantos estão prontos para finalizar. */}
                    <Flex h="4px" mb={3} mx={1} borderRadius="full" overflow="hidden" bg="gray.200">
                      {ehFinal && cards.length > 0 && (
                        <>
                          {prontos > 0 && (
                            <Tooltip hasArrow label={`${prontos} pronto(s) para finalizar`}>
                              <Box
                                h="100%"
                                w={`${(prontos / cards.length) * 100}%`}
                                bg={QUEM_META.nenhum.cor}
                              />
                            </Tooltip>
                          )}
                          {cards.length - prontos > 0 && (
                            <Tooltip
                              hasArrow
                              label={`${cards.length - prontos} com pendência na etapa`}
                            >
                              <Box
                                h="100%"
                                w={`${((cards.length - prontos) / cards.length) * 100}%`}
                                bg={RISCO_META[0].accent}
                              />
                            </Tooltip>
                          )}
                        </>
                      )}
                      {!ehFinal &&
                        cards.length > 0 &&
                        [
                          ...([3, 2, 1, 0] as const).map(n => ({
                            chave: String(n),
                            qtd: cards.filter(c => c.sla && nivelRisco(c.sla) === n).length,
                            rotulo: RISCO_META[n].rotulo.toLowerCase(),
                            cor: RISCO_META[n].accent,
                          })),
                          // Sem prazo fica fora do termômetro, como no selo do card.
                          {
                            chave: 'sem',
                            qtd: cards.filter(c => !c.sla).length,
                            rotulo: 'sem prazo',
                            cor: 'gray.300',
                          },
                        ]
                          .filter(f => f.qtd > 0)
                          .map(f => (
                            <Tooltip key={f.chave} hasArrow label={`${f.qtd} ${f.rotulo}`}>
                              <Box h="100%" w={`${(f.qtd / cards.length) * 100}%`} bg={f.cor} />
                            </Tooltip>
                          ))}
                    </Flex>
                    <Droppable droppableId={`col-${col.id}`}>
                      {(provided, snapshot) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          minH="8px"
                          borderRadius="lg"
                          transition="background .2s"
                          bg={snapshot.isDraggingOver ? 'blackAlpha.50' : 'transparent'}
                        >
                          {cards.map((card, i) => {
                            const leg = legByCardId.get(card.id) as ILegalizacao;
                            return (
                              <Draggable key={card.id} draggableId={`card-${card.id}`} index={i}>
                                {(dp, arraste) => (
                                  // Este wrapper é a alça do arraste e a ÚNICA parada de Tab do
                                  // card (o dnd já o deixa focável, com role=button). O Enter
                                  // abre o drawer daqui — não com o card levantado; o Espaço é
                                  // do dnd (pegar/soltar).
                                  <Box
                                    ref={dp.innerRef}
                                    {...dp.draggableProps}
                                    {...dp.dragHandleProps}
                                    mb={2}
                                    borderRadius="xl"
                                    _focusVisible={{
                                      outline: '2px solid',
                                      outlineColor: 'gray.600',
                                      outlineOffset: '2px',
                                    }}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter' && !arraste.isDragging) {
                                        e.preventDefault();
                                        onCardClick(card, leg);
                                      }
                                    }}
                                  >
                                    {ehFinal ? (
                                      <LegalizacaoCardEntregue
                                        card={card}
                                        legalizacao={leg}
                                        tipo={tipo}
                                        columnTitle={col.titulo}
                                        obsAberta={!!obsAbertas[card.id]}
                                        onClick={onCardClick}
                                        onToggleObs={alternarObs}
                                        onFinalizar={(c, l) => setFinalizando({ card: c, leg: l })}
                                      />
                                    ) : (
                                      <CardDoProcesso
                                        card={card}
                                        legalizacao={leg}
                                        tipo={tipo}
                                        columnTitle={col.titulo}
                                        obsAberta={!!obsAbertas[card.id]}
                                        onClick={onCardClick}
                                        onToggleObs={alternarObs}
                                      />
                                    )}
                                  </Box>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                          {cards.length === 0 && (
                            <Text
                              fontSize="11px"
                              color="gray.400"
                              fontStyle="italic"
                              textAlign="center"
                              py={3}
                            >
                              {/* Com filtro ativo a coluna não está vazia, está
                                escondida. Dizer "vazio" seria mentira sobre o dado. */}
                              {filtroAtivo ? '— sem resultado —' : '— vazio —'}
                            </Text>
                          )}
                        </Box>
                      )}
                    </Droppable>
                    {onIniciarAqui && !filtroTransitorio && (
                      <Button
                        onClick={() => onIniciarAqui(col.titulo)}
                        variant="unstyled"
                        w="full"
                        mt={1}
                        h="auto"
                        py={1.5}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap={1.5}
                        fontSize="11px"
                        fontWeight="500"
                        color="gray.400"
                        borderWidth="1px"
                        borderStyle="dashed"
                        borderColor="gray.300"
                        borderRadius="lg"
                        transition="all .12s"
                        _hover={{ color: cor.border, borderColor: cor.border, bg: 'white' }}
                      >
                        <Plus size={12} weight="bold" />
                        Iniciar aqui
                      </Button>
                    )}
                  </Box>
                );
              })}
            </Flex>
          </Box>
        </Box>
      </DragDropContext>

      {/* Modal de confirmação do move (drag gated). */}
      <Modal isOpen={!!confirm} onClose={() => setConfirm(null)} isCentered>
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl" bg="white">
          <ModalHeader fontSize="18px" fontWeight="700">
            {confirm?.direction === 'forward'
              ? `Avançar para "${confirm?.toCol}"?`
              : `Voltar para "${confirm?.toCol}"?`}
          </ModalHeader>
          <ModalBody>
            {forwardBloqueado ? (
              <Box>
                <Text fontSize="13px" color="orange.600" fontWeight="500" mb={2}>
                  Faltam itens obrigatórios em &quot;{confirm?.fromCol}&quot;:
                </Text>
                <Stack spacing={1}>
                  {confirm?.faltantes.map(f => (
                    <Text key={f} fontSize="13px" color="gray.600">
                      • {f}
                    </Text>
                  ))}
                </Stack>
              </Box>
            ) : (
              <Text fontSize="13px" color="gray.600">
                {confirm?.direction === 'forward'
                  ? 'Todos os itens obrigatórios estão OK. Confirmar o avanço?'
                  : 'Confirma voltar uma etapa? (não exige checklist)'}
              </Text>
            )}
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" borderRadius="full" onClick={() => setConfirm(null)}>
              Cancelar
            </Button>
            {forwardBloqueado ? (
              <Button
                colorScheme="blue"
                borderRadius="full"
                px={5}
                onClick={() => {
                  if (confirm) onCardClick(confirm.card, confirm.leg);
                  setConfirm(null);
                }}
              >
                Abrir para completar
              </Button>
            ) : (
              <Button
                colorScheme="blue"
                borderRadius="full"
                px={5}
                isLoading={moverMutation.isPending}
                onClick={() =>
                  confirm && moverMutation.mutate({ id: confirm.leg.id, toCol: confirm.toCol })
                }
              >
                Confirmar
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <FinalizarProcessoModal
        isOpen={!!finalizando}
        onClose={() => setFinalizando(null)}
        titulo={finalizando?.card.titulo}
        isLoading={finalizarMutation.isPending}
        onConfirm={() => finalizando && finalizarMutation.mutate(finalizando.leg.id)}
      />
    </>
  );
};

export default LegalizacaoBoard;
